import type { Dispatch, MutableRefObject, SetStateAction } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { computeRotationAngleSync } from '@/lib/boundaries/bounding-box-boundary'
import {
  type SmartGuideLine,
  getSingleShapeRotationGuideCenter,
  resolveSnappedRotationDegrees,
  snapRotationAngle,
} from '@/lib/guides/smart-guides'
import { getOtherObjectRotations } from '@/lib/guides/rotation-snap-helpers'
import { normalizeRotationDegrees, shortestDeltaDegrees } from '@/lib/transforms/geometry'
import { computeMultiRotatePreview } from '@/lib/transforms/multi-rotation'
import type { InteractionState } from '../types/interaction-state'
import type { MultiSelectChrome } from '@/lib/transforms/types/multi-select-chrome'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

/** Latest pointer position and modifier for one rotate step. */
export interface RotatePointer {
  clientX: number
  clientY: number
  shiftKey: boolean
}

/** A multi-select rotate — the fields `startInteraction` sets when more than one shape turns. */
export type MultiRotateInteraction = InteractionState &
  Required<Pick<InteractionState, 'multiRotateInitials' | 'rotatePivotLocal'>>

export interface RotateGestureDeps {
  /** Reads the live shape list; called after updates so it sees them. */
  getShapes: () => ShapeData[]
  smartGuidesEnabled: boolean
  emitSmartGuides: (guides: SmartGuideLine[]) => void
  updateShape: ShapeManagerRef['updateShape']
  setMultiSelectChrome: Dispatch<SetStateAction<MultiSelectChrome | null>>
  multiSelectChromeRef: MutableRefObject<MultiSelectChrome | null>
  scheduleLiveSelectionGeometrySync: () => void
  scheduleLiveWrapSync: () => void
}

/** Live multi-select rotate frame: orbit every shape about the chrome pivot, snapping the chrome angle. */
export function applyLiveMultiRotate(
  current: MultiRotateInteraction & { rotationStartAngle: number },
  latest: RotatePointer,
  cx: number,
  cy: number,
  {
    getShapes,
    smartGuidesEnabled,
    emitSmartGuides,
    updateShape,
    setMultiSelectChrome,
    multiSelectChromeRef,
    scheduleLiveSelectionGeometrySync,
    scheduleLiveWrapSync,
  }: RotateGestureDeps
): void {
  const angle = Math.atan2(latest.clientY - cy, latest.clientX - cx)
  const deltaRad = angle - current.rotationStartAngle
  const chromeRot0 = current.multiChromeRotationStartDeg

  let shapeDeltaDeg = (deltaRad * 180) / Math.PI

  if (chromeRot0 !== undefined) {
    const rawChromeDeg = normalizeRotationDegrees(chromeRot0 + shapeDeltaDeg)
    let rotationSnap: {
      snapped: number
      didSnap: boolean
      matchedOther: boolean
    } | null = null

    if (smartGuidesEnabled) {
      const multiRotateExcludeIds = new Set(
        current.multiRotateInitials ? Object.keys(current.multiRotateInitials) : []
      )
      const multiRotateOtherAngles = getOtherObjectRotations(multiRotateExcludeIds, getShapes(), [])

      rotationSnap = snapRotationAngle(rawChromeDeg, 5, multiRotateOtherAngles)

      if (rotationSnap.didSnap) {
        shapeDeltaDeg = shortestDeltaDegrees(chromeRot0, rotationSnap.snapped)
      }

      if (rotationSnap.didSnap) {
        emitSmartGuides([
          {
            type: 'rotation',
            position: rotationSnap.snapped,
            from: 0,
            to: 0,
            angle: rotationSnap.snapped,
            centerX: current.rotatePivotLocal.x,
            centerY: current.rotatePivotLocal.y,
          },
        ])
      } else {
        emitSmartGuides([])
      }
    }

    const updates = computeMultiRotatePreview({
      initials: current.multiRotateInitials,
      pivot: current.rotatePivotLocal,
      deltaDegrees: shapeDeltaDeg,
    })

    for (const [id, update] of Object.entries(updates)) {
      updateShape(id, update)
    }

    const displayChromeDeg = rotationSnap?.didSnap ? rotationSnap?.snapped : rawChromeDeg

    setMultiSelectChrome((prev) =>
      prev
        ? {
            ...prev,
            rotationDeg: displayChromeDeg,
          }
        : prev
    )
    const chromeSnapshot = multiSelectChromeRef.current

    if (chromeSnapshot) {
      multiSelectChromeRef.current = {
        ...chromeSnapshot,
        rotationDeg: displayChromeDeg,
      }
    }

    scheduleLiveSelectionGeometrySync()
    scheduleLiveWrapSync()
  } else {
    const updates = computeMultiRotatePreview({
      initials: current.multiRotateInitials,
      pivot: current.rotatePivotLocal,
      deltaDegrees: shapeDeltaDeg,
    })

    for (const [id, update] of Object.entries(updates)) {
      updateShape(id, update)
    }

    if (smartGuidesEnabled) {
      emitSmartGuides([])
    }

    scheduleLiveSelectionGeometrySync()
    scheduleLiveWrapSync()
  }
}

/** Live single-shape rotate frame, with shift/smart-guide angle snapping. */
export function applyLiveSingleRotate(
  current: InteractionState,
  latest: RotatePointer,
  cx: number,
  cy: number,
  {
    getShapes,
    smartGuidesEnabled,
    emitSmartGuides,
    updateShape,
    scheduleLiveSelectionGeometrySync,
    scheduleLiveWrapSync,
  }: Omit<RotateGestureDeps, 'setMultiSelectChrome' | 'multiSelectChromeRef'>
): void {
  const result = computeRotationAngleSync({
    centerX: cx,
    centerY: cy,
    mouseX: latest.clientX,
    mouseY: latest.clientY,
    startAngle: current.rotationStartAngle,
    initialRotation: current.initial.rotation ?? 0,
  })

  const singleRotateExcludeIds = new Set([current.shapeId])
  const singleRotateOtherAngles = getOtherObjectRotations(singleRotateExcludeIds, getShapes(), [])
  const resolved = resolveSnappedRotationDegrees(result.degrees, {
    shiftKey: latest.shiftKey,
    smartGuidesEnabled,
    otherAngles: singleRotateOtherAngles,
  })

  if (resolved.didSmartGuideSnap) {
    const shape = getShapes().find((s) => s.id === current.shapeId)

    if (shape) {
      const { centerX, centerY } = getSingleShapeRotationGuideCenter(shape)

      emitSmartGuides([
        {
          type: 'rotation',
          position: resolved.degrees,
          from: 0,
          to: 0,
          angle: resolved.degrees,
          centerX,
          centerY,
        },
      ])
    } else {
      emitSmartGuides([])
    }
  } else {
    emitSmartGuides([])
  }

  updateShape(current.shapeId, { rotation: resolved.degrees })
  scheduleLiveSelectionGeometrySync()
  scheduleLiveWrapSync()
}
